import { useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import { journeySteps } from '../data/journey';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function CharacterJourney() {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      journeySteps.forEach((_step, index) => {
        const el = stepRefs.current[index];
        if (!el) return;

        gsap.fromTo(
          el,
          { opacity: 0, y: 60, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 80%',
              end: 'top 30%',
              scrub: 1,
            },
          }
        );

        gsap.to(el.querySelector('.step-icon'), {
          rotation: 360,
          duration: 3,
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 2,
          },
        });
      });

      const pathEl = containerRef.current?.querySelector('.journey-path');
      if (pathEl) {
        gsap.fromTo(
          pathEl,
          { strokeDashoffset: 1000 },
          {
            strokeDashoffset: 0,
            duration: 3,
            ease: 'none',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1,
            },
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={containerRef}
      id="journey"
      className="character-journey"
      style={{
        minHeight: '100vh',
        padding: '6rem 2rem',
        position: 'relative',
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
      }}
      aria-labelledby="journey-title"
    >
      <svg
        className="journey-path"
        viewBox="0 0 100 100"
        style={{
          position: 'absolute',
          left: '50%',
          top: '10%',
          width: '4px',
          height: '80%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="pathGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="var(--accent-primary)" />
            <stop offset="14%" stopColor="var(--accent-secondary)" />
            <stop offset="28%" stopColor="var(--accent-warm)" />
            <stop offset="42%" stopColor="var(--accent-tertiary)" />
            <stop offset="56%" stopColor="var(--accent-secondary)" />
            <stop offset="70%" stopColor="var(--accent-primary)" />
            <stop offset="100%" stopColor="var(--accent-tertiary)" />
          </linearGradient>
        </defs>
        <path
          d="M50,0 C30,20 70,30 50,50 C30,70 70,80 50,100"
          fill="none"
          stroke="url(#pathGradient)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="1000"
          strokeDashoffset="1000"
        />
      </svg>

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <header className="section-header" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 id="journey-title" className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, var(--fg-primary), var(--accent-secondary))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {t('journey.title')}
          </h2>
        </header>

        <div className="journey-steps" style={{ position: 'relative' }}>
          {journeySteps.map((step, index) => (
            <div
              key={step.id}
              ref={el => { stepRefs.current[index] = el; }}
              className="journey-step"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2rem',
                marginBottom: '4rem',
                padding: '2rem',
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                position: 'relative',
                flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                transform: index % 2 === 0 ? 'translateX(-50px)' : 'translateX(50px)',
              }}
            >
              <div
                className="step-marker"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: step.color,
                  border: '3px solid var(--bg-primary)',
                  boxShadow: `0 0 0 4px ${step.color}40, 0 0 30px ${step.color}60`,
                  zIndex: 10,
                  flexShrink: 0,
                }}
              />

              <div
                className="step-content"
                style={{
                  flex: 1,
                  maxWidth: '45%',
                  textAlign: index % 2 === 0 ? 'right' : 'left',
                  padding: index % 2 === 0 ? '0 2rem 0 0' : '0 0 0 2rem',
                }}
              >
                <span
                  className="step-icon"
                  style={{
                    display: 'inline-block',
                    fontSize: '3rem',
                    marginBottom: '1rem',
                    filter: `drop-shadow(0 0 20px ${step.color})`,
                  }}
                >
                  {step.icon}
                </span>
                <h3 className="step-title" style={{
                  fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                  fontWeight: 600,
                  marginBottom: '0.75rem',
                  color: step.color,
                }}>
                  {t(step.titleKey)}
                </h3>
                <p className="step-description" style={{
                  fontSize: '1rem',
                  color: 'var(--fg-secondary)',
                  lineHeight: 1.7,
                }}>
                  {t(step.descriptionKey)}
                </p>
              </div>

              <div
                className="step-skills"
                style={{
                  flex: 1,
                  maxWidth: '45%',
                  textAlign: index % 2 === 0 ? 'left' : 'right',
                  padding: index % 2 === 0 ? '0 0 0 2rem' : '0 2rem 0 0',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                }}
              >
                {step.skills.map(skillId => (
                  <span
                    key={skillId}
                    className="skill-tag"
                    style={{
                      display: 'inline-block',
                      padding: '0.375rem 0.875rem',
                      background: `${step.color}20`,
                      border: `1px solid ${step.color}60`,
                      borderRadius: '100px',
                      fontSize: '0.75rem',
                      color: step.color,
                      fontWeight: 500,
                    }}
                  >
                    {skillId.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}